import { NaluriAccountError } from '@/errors';
import type { AccountUser, TokenPayload } from '@/types/account';
import { invariant } from '@/utils/invariant';
import { TokenStore } from '@/utils/tokenStore';
import version from '@/utils/version';

const API_KEY_HEADER = 'x-api-key';

export interface AccountOptions {
  apiKey: string;
  gatewayUrl: string;
  tokenStore: TokenStore;
  timeoutMs?: number;
  onRefreshTokenError?: () => void | Promise<void>;
}

export class AccountApi {
  private readonly apiKey: string;

  private readonly gatewayUrl: string;

  private readonly tokenStore: TokenStore;

  constructor({ apiKey, gatewayUrl, tokenStore }: AccountOptions) {
    this.apiKey = apiKey;
    this.gatewayUrl = gatewayUrl;

    this.tokenStore = tokenStore;
  }

  protected getDefaultHeaders() {
    return {
      [API_KEY_HEADER]: this.apiKey,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-nl-sdk-version': version,
      'User-Agent': `Naluri Video SDK /JS (${version}})`,
    };
  }

  public async login(authorizationCode: string) {
    invariant(authorizationCode, 'Authorization code is required');

    const response = await fetch(`${this.gatewayUrl}/auth/login`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({
        authorizationCode,
      }),
    });

    if (!response.ok) {
      throw new NaluriAccountError({
        message: 'Failed to login',
      });
    }

    const data = (await response.json()) as TokenPayload & {
      user: AccountUser;
    };

    this.tokenStore.setItem('accessToken', data.accessToken);
    this.tokenStore.setItem('refreshToken', data.refreshToken);

    return data;
  }

  public async refresh(refreshToken: string) {
    invariant(refreshToken, 'Refresh token is required');

    const response = await fetch(`${this.gatewayUrl}/auth/refresh`, {
      method: 'POST',
      headers: this.getDefaultHeaders(),
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (!response.ok) {
      throw new NaluriAccountError({
        message: 'Failed to refresh token',
      });
    }

    const data = (await response.json()) as TokenPayload;

    return data;
  }

  public async logout(refreshToken: string) {
    invariant(refreshToken, 'Refresh token is required');

    const response = await fetch(`${this.gatewayUrl}/auth/logout`, {
      method: 'POST',
      headers: {
        ...this.getDefaultHeaders(),
        [API_KEY_HEADER]: this.apiKey,
      },
      body: JSON.stringify({
        refreshToken,
      }),
    });

    if (!response.ok) {
      throw new NaluriAccountError({
        message: 'Failed to logout',
      });
    }

    return response.ok;
  }
}
